/**
 * AnimatedPosition behavior wrapper for animated mode
 *
 * Interpolates block positions during animation playback.
 * - For PlayerBlock: interpolates position along route control points
 * - For DrawingBlock: calculates path progress for "drawing out" effect
 */

import React, { cloneElement, useMemo } from 'react'
import { useBlockRenderContext } from '@/components/blocks'
import type { Point, BlockVisualProps } from '../../Types'
import type { PlayBlock_Union } from '@/types/blocks'
import { isPlayerBlock } from '@/types/blocks/canvas/Player'
import { isDrawingBlock } from '@/types/blocks/canvas/Drawing'
import type { ControlPointBlock } from '@/types/blocks/canvas/ControlPoint'
import { interpolateLine } from '@/utils/Animation.utils'
import { extractPoint } from '@/utils/Block.utils'

interface AnimatedPositionProps {
	blockId: string
	block: PlayBlock_Union
	children: React.ReactElement
	// Optional: child blocks needed for route calculation
	controlPoints?: ControlPointBlock[]
}

type PathSegment = {
	start: Point
	end: Point
	length: number
}

/**
 * Builds path segments from control points with calculated lengths.
 *
 * Creates segments between consecutive control points and calculates
 * the Euclidean distance for each segment. Returns both the segments
 * array and the total path length.
 *
 * @param controlPoints - Ordered control points to build segments from
 * @returns Object with segments array and totalLength
 */
function buildPathSegments(
	controlPoints: ControlPointBlock[]
): { segments: PathSegment[]; totalLength: number } {
	const segments: PathSegment[] = []
	let totalLength = 0

	for (let i = 0; i < controlPoints.length - 1; i++) {
		const start = controlPoints[i]!
		const end = controlPoints[i + 1]!

		const startPoint = extractPoint(start)
		const endPoint = extractPoint(end)

		if (!startPoint || !endPoint) {
			continue
		}

		const dx = endPoint.x - startPoint.x
		const dy = endPoint.y - startPoint.y
		const length = Math.sqrt(dx * dx + dy * dy)

		segments.push({ start: startPoint, end: endPoint, length })
		totalLength += length
	}

	return { segments, totalLength }
}

/**
 * Finds the segment containing the target progress and interpolates position.
 *
 * Given a target distance along the path (based on animation progress),
 * finds which segment contains that distance and returns the interpolated
 * position within that segment.
 *
 * @param segments - Path segments with calculated lengths
 * @param targetDistance - Target distance along path (0 to totalLength)
 * @returns Interpolated Point, or null if segments are empty
 */
function findSegmentForProgress(
	segments: PathSegment[],
	targetDistance: number
): Point | null {
	if (segments.length === 0) {
		return null
	}

	let accumulatedDistance = 0

	for (const segment of segments) {
		if (targetDistance <= accumulatedDistance + segment.length) {
			// This is the segment we want - interpolate within it
			const segmentProgress = segment.length > 0
				? (targetDistance - accumulatedDistance) / segment.length
				: 0

			return interpolateLine(segment.start, segment.end, segmentProgress)
		}
		accumulatedDistance += segment.length
	}

	// If we're past the end, return the last segment's end point
	return segments[segments.length - 1]!.end
}

/**
 * Calculates animated position for a player moving along a route.
 *
 * Interpolates the player's position along control points based on
 * animation progress (0-1). Handles edge cases like single points,
 * zero-length paths, and progress beyond the path end.
 *
 * @param controlPoints - Ordered control points defining the player's route
 * @param animationTime - Progress along animation (0-1)
 * @returns Interpolated position, or null if no valid control points
 */
function calculatePlayerAnimatedPosition(
	controlPoints: ControlPointBlock[],
	animationTime: number
): Point | null {
	if (!controlPoints || controlPoints.length === 0) {
		return null
	}

	// If only one point, stay at that position
	if (controlPoints.length === 1) {
		return extractPoint(controlPoints[0]!)
	}

	// Build path segments from control points
	const { segments, totalLength } = buildPathSegments(controlPoints)

	// Handle edge case: all points are at the same position
	if (totalLength === 0) {
		return extractPoint(controlPoints[0]!)
	}

	// Find position along path based on animation progress
	const targetDistance = animationTime * totalLength
	return findSegmentForProgress(segments, targetDistance)
}

/**
 * Makes a block's position animated based on animation timeline.
 *
 * In animated mode, blocks move along paths or have progress-based effects.
 * The animationTime from context (0-1) determines position along the route.
 *
 * @param blockId - Block identifier (required by BlockBehaviorProps interface)
 * @param block - The block to animate
 * @param children - Visual component to wrap
 * @param controlPoints - Optional control points for player route calculation
 *
 * @example
 * ```tsx
 * <AnimatedPosition blockId={block.id} block={block} controlPoints={points}>
 *   <PlayerVisual block={playerBlock} />
 * </AnimatedPosition>
 * ```
 */
export function AnimatedPosition({
	blockId,
	block,
	children,
	controlPoints = [],
}: AnimatedPositionProps) {
	const context = useBlockRenderContext()

	// Calculate animated position based on block type
	// Always call hooks unconditionally (Rules of Hooks)
	const animatedPosition = useMemo(() => {
		// Only calculate in animated mode
		if (context.mode !== 'animated' || context.animationTime === undefined) {
			return null
		}

		const animationTime = context.animationTime

		if (isPlayerBlock(block)) {
			// For players, interpolate position along control points
			return calculatePlayerAnimatedPosition(controlPoints, animationTime)
		}

		if (isDrawingBlock(block)) {
			// For drawings, return path progress for stroke-dashoffset effect
			return { pathProgress: animationTime }
		}

		// Other block types don't have animation
		return null
	}, [context.mode, context.animationTime, block, controlPoints])

	// Only apply animation in animated mode
	if (context.mode !== 'animated' || !animatedPosition) {
		return children
	}

	// Clone child and pass animated position with proper typing
	// Use BlockVisualProps to ensure type safety
	return cloneElement(
		children as React.ReactElement<BlockVisualProps>,
		{
			animatedPosition,
		} as Partial<BlockVisualProps>
	)
}
